import { Box, Button, Divider, Flex, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddAllOptionsButton } from '@/module/MinimalStandard/components/AddAllOptionsButton';
import { getMinStdIndicatorResultOptions } from '@/module/MinimalStandard/model/MinStdQuery';
import {
  Indicator,
  IndicatorItem,
  isMinStdIndicatorAbstractItem,
  isMinStdIndicatorArticleItem,
  isMinStdIndicatorAwardItem,
  isMinStdIndicatorBookItem,
  isMinStdIndicatorBooleanItem,
  isMinStdIndicatorChapterItem,
  isMinStdIndicatorCitationItem,
  isMinStdIndicatorCommitteeItem,
  isMinStdIndicatorCourseItem,
  isMinStdIndicatorFreeItem,
  isMinStdIndicatorIntervalItem,
  isMinStdIndicatorNumberItem,
  isMinStdIndicatorPatentItem,
  isMinStdIndicatorProceedingItem,
  isMinStdIndicatorProjectItem,
  isMinStdIndicatorTranslationItem,
} from '@/module/MinimalStandard/model/MinStdTypes';

import { Content, NoContentPlaceholder } from '@/core/components';

import { Id, assertUnreachable } from '@feature/utils';

import { MinStdIndicatorItemCard } from '../MinStdIndicatorItemCard';
import { MinStdIndicatorItemCreate } from '../MinStdIndicatorItemCreate';
import { MinStdIndicatorItemEdit } from '../MinStdIndicatorItemEdit';

interface MinStdItemYearGatheringProps {
  indicator: Indicator;
  authorId: Id;

  year: number | null;
  hasAddAll: boolean;
}

function extractSelectId(item: IndicatorItem): number | null {
  if (isMinStdIndicatorNumberItem(item)) {
    return null;
  }

  if (isMinStdIndicatorBooleanItem(item)) {
    return null;
  }

  if (isMinStdIndicatorFreeItem(item)) {
    return null;
  }

  if (isMinStdIndicatorIntervalItem(item)) {
    return null;
  }

  if (isMinStdIndicatorProceedingItem(item)) {
    return item.proceeding.id;
  }

  if (isMinStdIndicatorArticleItem(item)) {
    return item.article.id;
  }

  if (isMinStdIndicatorCourseItem(item)) {
    return item.course.id;
  }

  if (isMinStdIndicatorAbstractItem(item)) {
    return item.abstractItem.id;
  }

  if (isMinStdIndicatorBookItem(item)) {
    return item.book.id;
  }

  if (isMinStdIndicatorChapterItem(item)) {
    return item.chapter.id;
  }

  if (isMinStdIndicatorCommitteeItem(item)) {
    return item.committee.id;
  }

  if (isMinStdIndicatorPatentItem(item)) {
    return item.patent.id;
  }

  if (isMinStdIndicatorProjectItem(item)) {
    return item.project.id;
  }

  if (isMinStdIndicatorTranslationItem(item)) {
    return item.translation.id;
  }

  if (isMinStdIndicatorAwardItem(item)) {
    return item.award.id;
  }

  if (isMinStdIndicatorCitationItem(item)) {
    return item.citation.id;
  }

  assertUnreachable(item);
}

export function MinStdItemYearGathering(props: MinStdItemYearGatheringProps) {
  const { indicator, authorId, year, hasAddAll } = props;
  const { data } = useSuspenseQuery(getMinStdIndicatorResultOptions(authorId, indicator.indicatorCode, null, year));
  const [isAdding, { open: openAdd, close: closeAdd }] = useDisclosure(false);

  const [editItem, setEditItem] = useState<IndicatorItem | null>(null);
  const closeEdit = () => setEditItem(null);
  const isEditing = editItem != null;

  const { items, totalScore } = data;
  const alreadySelectedIds = useMemo(() => items.map(extractSelectId).filter(id => id != null), [items]);
  const { t } = useTranslation();

  if (isEditing) {
    return (
      <MinStdIndicatorItemEdit
        editItem={editItem}
        close={closeEdit}
        authorId={authorId}
        year={year}
        indicatorCode={indicator.indicatorCode}
        indicatorType={indicator.indicatorType}
        legend={indicator.legend}
        alreadySelectedIds={alreadySelectedIds}
        showPointsField={indicator.manualPoints}
      />
    );
  }

  if (isAdding) {
    return (
      <MinStdIndicatorItemCreate
        close={closeAdd}
        year={year}
        authorId={authorId}
        indicatorCode={indicator.indicatorCode}
        indicatorType={indicator.indicatorType}
        legend={indicator.legend}
        alreadySelectedIds={alreadySelectedIds}
        showPointsField={indicator.manualPoints}
      />
    );
  }

  return (
    <Box
      p="sm"
      component={Stack}
    >
      <Flex
        align="center"
        justify="space-between"
      >
        <Stack gap="2px">
          <Text
            size="sm"
            fw="500"
          >
            Total punctaj indicator {year}: {totalScore}
          </Text>
        </Stack>
        <Group>
          {hasAddAll && (
            <AddAllOptionsButton
              authorId={authorId}
              indicatorCode={indicator.indicatorCode}
              year={year}
            />
          )}
          <Button
            size="xs"
            onClick={openAdd}
          >
            {t('button.add')}
          </Button>
        </Group>
      </Flex>
      <Divider />
      {items.length === 0 && <NoContentPlaceholder title={t('minStd.noIndicatorItems')} />}
      <Stack gap="xl">
        {items.length > 0 &&
          items.map((item, index) => (
            <Stack
              component={Content}
              gap="3px"
              key={item.id}
            >
              <Flex gap="3px">
                <MinStdIndicatorItemCard
                  index={index}
                  item={item}
                  authorId={authorId}
                  indicatorCode={indicator.indicatorCode}
                  legend={indicator.legend}
                  onEdit={setEditItem}
                />
              </Flex>
            </Stack>
          ))}
      </Stack>
    </Box>
  );
}
